import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { FormField } from "@/shared/ui/form-field";
import { ArrowLeft, Copy, Download, Upload, Plus, X } from "lucide-react";
import { useToast } from "@/shared/hooks/use-toast";
import { useLanguage } from "@/shared/hooks/use-language";
import { ScopeContextStorage } from "@/shared/lib/scope-storage";
import { exitToLogin } from '@/shared/lib/session-exit';
import { parseBackendErrors } from "@/shared/lib/backend-errors";
import type { ScopeContext } from "@/features/auth";
import { getRoles, type Role } from "@/features/auth/api/role";
import { createIntegration, type Integration } from "@/features/auth/api/integration";
import { isUnreadableResponseError } from "@/shared/lib/response-shape";
import { generateSessionKeyPair, privateKeyToPem } from "@/shared/lib/crypto";
import { DebugLogger } from "@/shared/lib/debug";

const NEW_INTEGRATION_FIELD_MAP: Record<string, string> = {
  name: "integrationName",
  role_ids: "roles",
  allowed_ips: "allowedIPs",
  public_key: "publicKey",
};

const SECOND_DOWNLOAD_DELAY_MS = 100;
const IMPORTED_KEY_RETURN_DELAY_MS = 1500;
const OBJECT_URL_REVOKE_DELAY_MS = 60 * 1000;

const KEY_TEXTAREA_CLASS_NAME =
  "flex w-full resize-none rounded-md border border-border bg-background px-3 py-2 font-mono text-sm whitespace-pre overflow-x-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

type GeneratedKeys = { publicKey: string; privateKey: string };

const NewIntegration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { toast } = useToast();
  const sessionId = location.state?.sessionId || ScopeContextStorage.getCurrentSessionId();
  const passedScopeContext = location.state?.scopeContext;

  const [integrationName, setIntegrationName] = useState("");
  const [allowedIPs, setAllowedIPs] = useState("");
  const [keyOption, setKeyOption] = useState<"generate" | "import">("generate");
  const [importedPublicKey, setImportedPublicKey] = useState("");
  const [publicKeyFile, setPublicKeyFile] = useState<File | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<Array<{ id: string; name: string; display_name?: string }>>([{ id: '', name: '' }]);
  const [keys, setKeys] = useState<GeneratedKeys | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createdIntegration, setCreatedIntegration] = useState<Integration | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const isMountedRef = useRef(false);
  const returnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (returnTimerRef.current) clearTimeout(returnTimerRef.current);
    };
  }, []);

  const clearFieldError = (field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  useEffect(() => {
    if (!sessionId) {
      toast({
        title: t("integrations.error") || "Error",
        description: "No session ID found. Please log in again.",
        variant: "destructive",
      });
      exitToLogin();
      return;
    }

    loadRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const loadRoles = async () => {
    try {
      setIsLoadingRoles(true);
      const rolesResponse = await getRoles(sessionId || '');
      const selectableRoles = (rolesResponse.roles || []).filter(
        (role: Role) => role.type === "default" || role.type === "custom"
      );
      setRoles(selectableRoles);
    } catch (err) {
      DebugLogger.error('Failed to load roles', { 
        error: err instanceof Error ? err.message : 'Unknown error' 
      });
      toast({
        title: t("integrations.error") || "Error",
        description: err instanceof Error ? err.message : 'Failed to load roles',
        variant: "destructive",
      });
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const handleAddRole = () => {
    setSelectedRoles([...selectedRoles, { id: '', name: '' }]);
  };

  const handleRemoveRole = (index: number) => {
    setSelectedRoles(selectedRoles.filter((_, i) => i !== index));
  };

  const handleRoleChange = (index: number, roleId: string) => {
    clearFieldError("roles");
    if (!roleId) {
      const updatedRoles = [...selectedRoles];
      updatedRoles[index] = { id: '', name: '' };
      setSelectedRoles(updatedRoles);
      return;
    }

    const selectedRole = roles.find(r => r.id === roleId);
    if (selectedRole) {
      const updatedRoles = [...selectedRoles];
      updatedRoles[index] = { id: selectedRole.id, name: selectedRole.display_name || selectedRole.name };
      setSelectedRoles(updatedRoles);
    }
  };

  const getAvailableRoles = (currentIndex: number) => {
    const selectedRoleIds = selectedRoles
      .map((role, index) => index !== currentIndex && role.id ? role.id : null)
      .filter(id => id !== '' && id !== null) as string[];
    
    return roles.filter(role => !selectedRoleIds.includes(role.id));
  };

  const getRolesForSelect = (index: number) => {
    const currentRole = selectedRoles[index];
    const availableRoles = getAvailableRoles(index);
    
    if (currentRole && currentRole.id && currentRole.name && !availableRoles.find(r => r.id === currentRole.id)) {
      const fullCurrentRole = roles.find(r => r.id === currentRole.id);
      if (fullCurrentRole) {
        return [{ id: fullCurrentRole.id, name: fullCurrentRole.display_name || fullCurrentRole.name }, ...availableRoles];
      }
      return [currentRole, ...availableRoles];
    }
    
    return availableRoles;
  };

  const handleCreateIntegration = async (e: React.FormEvent) => {
    e.preventDefault();
    if (createdIntegration) return;

    const localErrors: Record<string, string> = {};
    if (!integrationName.trim()) {
      localErrors.integrationName = t("integrations.nameRequired") || "Please enter a name for the integration";
    }
    const validRoleIds = selectedRoles
      .filter(role => role.id && role.id !== '')
      .map(role => role.id);
    if (validRoleIds.length === 0) {
      localErrors.roles = t("integrations.roleRequired") || "Please select at least one role";
    }
    if (keyOption === "import" && !publicKeyFile && !importedPublicKey.trim()) {
      localErrors.publicKey = t("integrations.publicKeyRequired") || "Please upload or paste your public key";
    }
    if (Object.keys(localErrors).length > 0) {
      setFieldErrors(localErrors);
      setError(null);
      return;
    }

    try {
      setIsCreating(true);
      setError(null);
      setFieldErrors({});

      if (keyOption === "generate") {
        const { privateKey, publicKey } = generateSessionKeyPair();
        const generatedKeys: GeneratedKeys = {
          publicKey: publicKey.toPem(),
          privateKey: privateKeyToPem(privateKey),
        };
        await submitIntegration(generatedKeys.publicKey, generatedKeys);
        return;
      }

      const importedKey = publicKeyFile ? await publicKeyFile.text() : importedPublicKey.trim();
      await submitIntegration(importedKey, null);
    } catch (err) {
      DebugLogger.error('Failed to create integration', {
        error: err instanceof Error ? err.message : 'Unknown error'
      });
      const errorMessage = isUnreadableResponseError(err)
        ? t("integrations.createUnreadable")
        : err instanceof Error ? err.message : 'Failed to create integration';
      const { fieldErrors: backendFieldErrors, generalError } = parseBackendErrors(
        err,
        NEW_INTEGRATION_FIELD_MAP,
      );
      if (Object.keys(backendFieldErrors).length > 0) {
        setFieldErrors(backendFieldErrors);
        setError(generalError);
      } else {
        setError(errorMessage);
      }
      toast({
        title: t("integrations.createError") || "Error",
        description: errorMessage,
        variant: "destructive",
      });
      setIsCreating(false);
    }
  };

  const submitIntegration = async (publicKeyPem: string, generatedKeys: GeneratedKeys | null) => {
    try {
      const contextToUse = passedScopeContext || ScopeContextStorage.getScopeContext(sessionId || '');
      if (!contextToUse) {
        throw new Error('No scope context available');
      }

      if (!contextToUse.account) {
        throw new Error('Account is required to create integration');
      }

      const scopeContext: ScopeContext = {
        scopeId: contextToUse.account.id,
        scopeType: 'account'
      };

      const allowedIPsArray = allowedIPs
        .split(/[,\n]/)
        .map(ip => ip.trim())
        .filter(ip => ip.length > 0);

      const validRoleIds = selectedRoles
        .filter(role => role.id && role.id !== '')
        .map(role => role.id);

      const request = {
        integrations: [
          {
            name: integrationName.trim(),
            role_ids: validRoleIds,
            allowed_ips: allowedIPsArray.length > 0 ? allowedIPsArray : undefined,
            public_key: publicKeyPem
          }
        ]
      };

      const integration = await createIntegration(sessionId || '', request, scopeContext);

      setCreatedIntegration(integration);
      toast({
        title: t("integrations.createSuccess") || "Integration created",
        description: t("integrations.createSuccessDesc") || "The integration has been successfully created.",
      });

      if (generatedKeys) {
        setKeys(generatedKeys);
        return;
      }

      if (isMountedRef.current) {
        returnTimerRef.current = setTimeout(goToIntegrations, IMPORTED_KEY_RETURN_DELAY_MS);
      }
    } catch (err) {
      DebugLogger.error('Failed to submit integration', { 
        error: err instanceof Error ? err.message : 'Unknown error' 
      });
      throw err;
    } finally {
      setIsCreating(false);
    }
  };

  const downloadPem = (content: string, fileName: string) => {
    const url = URL.createObjectURL(new Blob([content], { type: "application/x-pem-file" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_REVOKE_DELAY_MS);
  };

  const downloadKeys = (publicKey: string, privateKey: string, integrationId: string) => {
    downloadPem(publicKey, `${integrationId}-public-key.pem`);
    setTimeout(() => {
      downloadPem(privateKey, `${integrationId}-private-key.pem`);
      toast({
        title: t("integrations.keysDownloaded") || "Keys downloaded",
        description: t("integrations.keysDownloadedDesc") || "Your keys have been downloaded",
      });
    }, SECOND_DOWNLOAD_DELAY_MS);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: t("integrations.copied") || "Copied!",
      description: `${label} ${t("integrations.copiedToClipboard") || "copied to clipboard"}`,
    });
  };

  const goToIntegrations = () => {
    navigate("/console/integrations", {
      state: {
        sessionId,
        scopeContext: passedScopeContext || ScopeContextStorage.getScopeContext(sessionId || '')
      }
    });
  };

  if (createdIntegration && keys) {
    return (
      <main className="flex-1 p-4 md:p-8 animate-fadeIn">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <Button
                variant="ghost"
                onClick={goToIntegrations}
                className="gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                {t("integrations.backToIntegrations") || "Back to Integrations"}
              </Button>
            </div>

            <Card className="bg-card border-border p-8" data-testid="integration-created">
              <div className="space-y-6">
                <div className="text-center pb-6 border-b border-border">
                  <h2 className="text-2xl font-bold text-foreground mb-2">
                    {t("integrations.keysGenerated") || "Keys Generated!"}
                  </h2>
                  <p className="text-muted-foreground">
                    {t("integrations.keysGeneratedDesc") || "Save your keys in a secure location. The private key will not be displayed again."}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    {t("integrations.publicKeyLabel") || "Public Key"}
                  </label>
                  <div className="flex gap-2">
                    <textarea
                      value={keys.publicKey}
                      readOnly
                      rows={keys.publicKey.split("\n").length}
                      spellCheck={false}
                      data-testid="integration-created-public-key"
                      className={`${KEY_TEXTAREA_CLASS_NAME} text-foreground`}
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(keys.publicKey, t("integrations.publicKeyLabel") || "Public key")}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    {t("integrations.privateKey") || "Private Key"}
                  </label>
                  <div className="flex gap-2">
                    <textarea
                      value={keys.privateKey}
                      readOnly
                      rows={keys.privateKey.split("\n").length}
                      spellCheck={false}
                      data-testid="integration-created-private-key"
                      className={`${KEY_TEXTAREA_CLASS_NAME} text-red-500`}
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(keys.privateKey, t("integrations.privateKey") || "Private key")}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-red-500 mt-2">
                    ⚠️ {t("integrations.privateKeyWarning") || "Keep this key in a secure location. It will not be displayed again."}
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    className="flex-1"
                    data-testid="integration-created-download"
                    onClick={() => downloadKeys(keys.publicKey, keys.privateKey, createdIntegration.id)}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {t("integrations.downloadKeys") || "Download Keys"}
                  </Button>
                  <Button
                    className="flex-1 bg-primary hover:bg-primary/90"
                    data-testid="integration-created-finish"
                    onClick={goToIntegrations}
                  >
                    {t("integrations.finish") || "Finish"}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </main>
    );
  }

  return (
    <main className="flex-1 p-4 md:p-8 animate-fadeIn">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              onClick={goToIntegrations}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("integrations.backToIntegrations") || "Back to Integrations"}
            </Button>
          </div>

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              {t("integrations.newIntegration") || "New Integration"}
            </h1>
            <p className="text-muted-foreground">
              {t("integrations.newIntegrationDesc") || "Create a new integration with ECDSA keys"}
            </p>
          </div>

          <Card className="bg-card border-border p-8">
            <form onSubmit={handleCreateIntegration} className="space-y-6" data-testid="integration-new-form">
              {error && (
                <div data-testid="integration-new-error" className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <FormField
                id="integrationName"
                type="text"
                label={t("integrations.integrationNameLabel") || "Integration Name"}
                placeholder={t("integrations.integrationNamePlaceholder") || "Ex: Payment API"}
                value={integrationName}
                onChange={(value) => {
                  setIntegrationName(value);
                  clearFieldError("integrationName");
                }}
                error={fieldErrors.integrationName}
                required
              />

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  {t("integrations.profiles") || "Roles"}
                </label>
                <div className="space-y-3">
                  {selectedRoles.map((role, index) => (
                    <Card key={index} className="bg-card border-border p-4">
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <select
                            value={role.id}
                            onChange={(e) => handleRoleChange(index, e.target.value)}
                            data-testid="integration-new-role"
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={isLoadingRoles}
                            required={index === 0}
                          >
                            <option value="">
                              {isLoadingRoles 
                                ? (t("integrations.loadingRoles") || "Loading roles...")
                                : (t("integrations.selectProfile") || "Select role")
                              }
                            </option>
                            {getRolesForSelect(index).map((availableRole) => (
                              <option key={availableRole.id} value={availableRole.id}>
                                {availableRole.display_name || availableRole.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        {selectedRoles.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            type="button"
                            className="text-destructive hover:text-destructive/90 hover:bg-destructive/10"
                            onClick={() => handleRemoveRole(index)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full mt-4 gap-2"
                  onClick={handleAddRole}
                  disabled={isLoadingRoles || getAvailableRoles(-1).length === 0}
                >
                  <Plus className="w-4 h-4" />
                  {t("integrations.addProfile") || "Add Role"}
                </Button>
                {fieldErrors.roles && (
                  <p className="text-xs text-destructive mt-2">{fieldErrors.roles}</p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  {t("integrations.allowedIPs") || "Allowed IPs"} ({t("integrations.optional") || "optional"})
                </label>
                <textarea
                  placeholder={t("integrations.allowedIPsPlaceholder") || "Ex: 192.168.1.1, 10.0.0.5\nOne IP per line or separated by comma"}
                  value={allowedIPs}
                  onChange={(e) => setAllowedIPs(e.target.value)}
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  rows={3}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {t("integrations.allowedIPsHint") || "Leave blank to allow all IPs"}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  {t("integrations.keyConfiguration") || "Key Configuration"}
                </label>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="generate"
                      name="keyOption"
                      value="generate"
                      checked={keyOption === "generate"}
                      onChange={(e) => setKeyOption(e.target.value as "generate" | "import")}
                      className="w-4 h-4"
                    />
                    <label htmlFor="generate" className="text-sm cursor-pointer">
                      {t("integrations.generateKeys") || "Generate keys automatically"}
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="import"
                      name="keyOption"
                      value="import"
                      checked={keyOption === "import"}
                      onChange={(e) => setKeyOption(e.target.value as "generate" | "import")}
                      className="w-4 h-4"
                    />
                    <label htmlFor="import" className="text-sm cursor-pointer">
                      {t("integrations.importPublicKey") || "Import my public key"}
                    </label>
                  </div>
                </div>

                {fieldErrors.publicKey && (
                  <p className="text-xs text-destructive mt-2">{fieldErrors.publicKey}</p>
                )}

                {keyOption === "import" && (
                  <div className="mt-4 space-y-4">
                    <div className="flex items-center justify-center w-full">
                      <label 
                        className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-background hover:bg-accent border-border"
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onDragEnter={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const file = e.dataTransfer.files[0];
                          if (file && (file.name.endsWith('.pem') || file.name.endsWith('.pub') || file.name.endsWith('.txt'))) {
                            setPublicKeyFile(file);
                          }
                        }}
                      >
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <Upload className="w-8 h-8 mb-3 text-muted-foreground" />
                          <p className="mb-2 text-sm text-muted-foreground">
                            {publicKeyFile ? (
                              <span className="font-semibold">{publicKeyFile.name}</span>
                            ) : (
                              <>
                                <span className="font-semibold">{t("integrations.clickToUpload") || "Click to upload"}</span> {t("integrations.orDragFile") || "or drag file"}
                              </>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t("integrations.fileTypes") || "File .pem, .pub or .txt"}
                          </p>
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept=".pem,.pub,.txt"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setPublicKeyFile(file);
                            }
                          }}
                        />
                      </label>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        {t("integrations.orPasteKey") || "Or paste your public key"}
                      </label>
                      <textarea
                        placeholder={t("integrations.pastePublicKeyPlaceholder") || "-----BEGIN PUBLIC KEY-----\n...\n-----END PUBLIC KEY-----"}
                        value={importedPublicKey}
                        onChange={(e) => setImportedPublicKey(e.target.value)}
                        data-testid="integration-new-public-key"
                        className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        rows={4}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                <h3 className="text-sm font-semibold text-foreground mb-2">
                  {t("integrations.aboutECDSAKeys") || "About ECDSA Keys"}
                </h3>
                <p className="text-sm text-muted-foreground mb-2">
                  {keyOption === "generate" 
                    ? (t("integrations.aboutECDSAKeysGenerate") || "The system will generate a pair of ECDSA (Elliptic Curve Digital Signature Algorithm) cryptographic keys and upload the public key to us:")
                    : (t("integrations.aboutECDSAKeysImport") || "You will import your own ECDSA public key and we will use it to validate the digital signature of your Veltiz requests:")
                  }
                </p>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  {keyOption === "generate" ? (
                    <>
                      <li><strong>{t("integrations.privateKey") || "Private Key"}:</strong> {t("integrations.privateKeyDesc") || "Keep it secret and NEVER share it with anyone. You will use it to sign all Veltiz requests."}</li>
                      <li><strong>{t("integrations.publicKeyLabel") || "Public Key"}:</strong> {t("integrations.publicKeyUsageDesc") || "We will use it to validate the digital signature of your Veltiz requests."}</li>
                    </>
                  ) : (
                    <li><strong>{t("integrations.publicKeyLabel") || "Public Key"}:</strong> {t("integrations.publicKeyUsageDesc") || "We will use it to validate the digital signature of your Veltiz requests."}</li>
                  )}
                </ul>
              </div>

              <Button
                type="submit"
                data-testid="integration-new-submit"
                className="w-full bg-primary hover:bg-primary/90"
                disabled={isCreating || createdIntegration !== null}
              >
                {isCreating 
                  ? (t("integrations.creating") || "Creating...")
                  : keyOption === "generate" 
                    ? (t("integrations.generateAndCreate") || "Generate Keys and Create Integration")
                    : (t("integrations.createIntegration") || "Create Integration")
                }
              </Button>
            </form>
          </Card>
        </div>
      </main>
  );
};

export default NewIntegration;

